import React, { FC, useMemo } from 'react';
import moment from 'moment';

import { Routes } from '@common/constants';
import SectionContainer from '@common/components/SectionContainer';
import { useRouter } from 'shared-routing';
import { useTranslations } from '@lang';
import { useCheckForArrivalDate } from '@common/hooks';
import { DateCategoryPicker } from '@features/JourneyPicker';
import {
  EntitiesListingWrapper,
  JourneyPickerInnerWrapper,
  JourneyPickerOuterWrapper,
} from '@features/EntitiesListing/EntitiesListings.styles';

import { useGetSpaCategories } from '@features/spa/hooks/useGetSpaCategories';

import { SpaListTemplate } from 'voyager';

import { SpaDaysV2Props } from './SpaDaysV2.types';
import { useSpaDaysFilter } from './hooks/useSpaDaysFilter';
import { useSpaDaysList } from './hooks/useSpaDaysList';

export const SpaDaysV2: FC<SpaDaysV2Props> = ({
  property,
  journeyParams,
  changeJourneyParam,
  categoryId = null,
  language,
}) => {
  const router = useRouter();
  const { t } = useTranslations();
  const { arrivalDate } = router.query;

  useCheckForArrivalDate({
    newArrivalDate: journeyParams.fromDate as string,
    router,
    route: Routes.SPA_DAY_LIST,
  });

  const filter = useSpaDaysFilter({
    categoryId,
    arrivalDate: arrivalDate as string | undefined,
    fromDate: journeyParams?.fromDate as string | undefined,
    changeJourneyParam,
  });

  const { listItems, isFetching } = useSpaDaysList({
    propertyId: property.id,
    language,
    categoryFilterId: filter.categoryFilterId,
    date: filter.appliedDateString,
  });

  const { data: spaPackagesCategories = [] } = useGetSpaCategories(
    property.id,
    'packages',
    language
  );

  const allCategoriesOption = {
    label: t('common.allCategories'),
    value: null,
  };

  const categoryOptions = [
    allCategoriesOption,
    ...spaPackagesCategories.map((category) => ({
      label: category.name,
      value: category.id,
    })),
  ];

  const currentSelectedCategory = categoryOptions.find(
    ({ value }) => String(value) === String(filter.selectedCategory)
  );

  const handleSelectSpaDay = (id: string) => {
    router.push({ pathname: Routes.SPA_DAY_DETAIL, query: { id } });
  };

  const durationOptions = useMemo(() => {
    const seen = new Set<string>();
    return listItems.reduce<{ label: string; value: string }[]>((acc, item) => {
      if (item.durationValue && !seen.has(item.durationValue)) {
        seen.add(item.durationValue);
        acc.push({
          label: t(item.durationValue as Parameters<typeof t>[0]),
          value: item.durationValue,
        });
      }
      return acc;
    }, []);
  }, [listItems, t]);

  return (
    <EntitiesListingWrapper>
      <JourneyPickerOuterWrapper>
        <JourneyPickerInnerWrapper>
          <DateCategoryPicker
            fromDate={moment(filter.pickerDate)}
            onDateChange={(d) => filter.setPickerDate(d.format())}
            categories={categoryOptions}
            onCategoryChange={filter.handleCategoryChange}
            selectedCategory={currentSelectedCategory}
            handleUpdate={filter.handleUpdate}
          />
        </JourneyPickerInnerWrapper>
      </JourneyPickerOuterWrapper>
      <SectionContainer>
        <SpaListTemplate
          items={isFetching ? [] : listItems}
          onSelectItem={handleSelectSpaDay}
          isLoading={isFetching}
          titleLabel={t('category.spaDays')}
          fromPriceLabel={t('common.from')}
          allDurationsLabel={t('spa.allDurations')}
          soldOutLabel={t('spa.soldOut')}
          noResultsLabel={t('spa.noResults')}
          noImageLabel={t('spa.noImage')}
          formatGuests={(n) => t('common.numberOfGuests', { guestsAmount: n })}
          durationOptions={durationOptions}
        />
      </SectionContainer>
    </EntitiesListingWrapper>
  );
};
